import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface MemberQueryParams {
  firstName?: string;
  company?: string;
  lastName?: string;
  gender?: 'M' | 'F';
  financialStatus?: 'financial' | 'unfinancial' | 'non_fee_paying';
  status?: 'active';
  isMember?: boolean;
  limit?: number;
}

export interface Member {
  ID?: string;
  Chapter?: string;
  FullName?: string;
  MemberType?: string;
  Company?: string;
  [key: string]: any;
}

export interface QueryResponse {
  success: boolean;
  count: number;
  members: Member[];
  totalCount?: number;
  error?: string;
}

export interface ContactLookupParams {
  firstName?: string;
  lastName?: string;
}

export interface AddInteractionParams {
  memberId: string;
  note: string;
  date?: string;
  type?: string;
}

export interface InteractionResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface AddSiteVisitParams {
  worksiteId: string;
  date?: string;
}

export interface SiteVisitResponse {
  success: boolean;
  message: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImisApiService {
  private baseUrl = environment.imis.baseUrl.replace(/\/$/, '');
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private authenticated = false;
  private loggedInUserId: string | null = null;
  private loggedInUserName: string | null = null;

  constructor(private http: HttpClient) {}

  authenticate(): Observable<boolean> {
    if (environment.production) {
      // Production: iMIS session cookie + RequestVerificationToken interceptor handles auth
      return this.fetchLoggedInUserId().pipe(
        map(() => { this.authenticated = true; return true; }),
        catchError(() => { this.authenticated = true; return of(true); })
      );
    }

    // Development: OAuth2 password grant via proxy
    const authUrl = `${this.baseUrl}/token`;
    console.log('Attempting authentication to:', authUrl);
    console.log('Username:', environment.imis.username);

    const body = new URLSearchParams({
      grant_type: 'password',
      username: environment.imis.username,
      password: environment.imis.password
    });

    return this.http.post<any>(authUrl, body.toString(), {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    }).pipe(
      switchMap(response => {
        this.tokenSubject.next(response.access_token);
        this.authenticated = true;
        console.log('Authentication successful');
        return this.fetchLoggedInUserId();
      }),
      map(() => true),
      catchError(error => {
        console.error('Authentication failed:', error);
        console.error('Status:', error.status);
        console.error('Message:', error.message);
        console.error('Error details:', error.error);
        return throwError(() => new Error('Authentication failed'));
      })
    );
  }

  private getHeaders(): HttpHeaders {
    if (environment.production) {
      return new HttpHeaders({ 'Content-Type': 'application/json' });
    }
    return new HttpHeaders({
      'Authorization': `Bearer ${this.tokenSubject.value}`,
      'Content-Type': 'application/json'
    });
  }

  private ensureAuthenticated<T>(operation: () => Observable<T>): Observable<T> {
    if (this.authenticated) {
      return operation();
    }
    return this.authenticate().pipe(switchMap(() => operation()));
  }

  private fetchLoggedInUserId(): Observable<void> {
    // Production: host ASP.NET app injects __ClientContext with loggedInPartyId (same pattern as Meeting Roster)
    if (environment.production) {
      try {
        const el = document.getElementById('__ClientContext') as HTMLInputElement | null;
        if (el?.value) {
          const ctx = JSON.parse(el.value);
          this.loggedInUserId = ctx.selectedPartyId !== ctx.loggedInPartyId
            ? ctx.selectedPartyId
            : (ctx.loggedInPartyId ?? null);
          this.loggedInUserName = ctx.loggedInName || ctx.selectedName || null;
          console.log('👤 Logged-in user iMIS ID (from __ClientContext):', this.loggedInUserId);
        } else {
          console.warn('⚠️ __ClientContext element not found — LeadStaff will not be set');
        }
      } catch (e) {
        console.warn('⚠️ Failed to parse __ClientContext:', e);
      }
      return this.loggedInUserId && !this.loggedInUserName
        ? this.fetchLoggedInUserName()
        : of(undefined);
    }

    // Dev: hardcoded fallback
    this.loggedInUserId = '103910';
    return this.fetchLoggedInUserName();
  }

  private fetchLoggedInUserName(): Observable<void> {
    if (!this.loggedInUserId) return of(undefined);
    return this.http.get<any>(`${this.baseUrl}/api/party/${this.loggedInUserId}`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => {
        const first = response.PersonName?.FirstName || '';
        const last  = response.PersonName?.LastName  || '';
        this.loggedInUserName = (first || last)
          ? `${first} ${last}`.trim()
          : (response.Name || null);
        console.log('👤 Logged-in user name:', this.loggedInUserName);
      }),
      catchError(error => {
        console.warn('⚠️ Could not fetch user name:', error.message);
        return of(undefined);
      })
    );
  }

  getLoggedInUserId(): string | null {
    return this.loggedInUserId;
  }

  getLoggedInUserName(): string | null {
    return this.loggedInUserName;
  }

  lookupWorksite(name: string): Observable<QueryResponse> {
    return this.ensureAuthenticated(() => {
      const httpParams = new HttpParams()
        .set('QueryName', environment.imis.worksiteLookupQueryName)
        .append('parameter', name)
        .append('Limit', '50');

      console.log('🏢 Worksite lookup:', name);

      return this.http.get<any>(`${this.baseUrl}/api/iqa`, {
        headers: this.getHeaders(),
        params: httpParams
      }).pipe(
        map(data => {
          const itemsArray = data.Items?.$values || data.Items || [];
          const worksites = itemsArray.map((item: any) => {
            const worksite: any = {};
            const propsArray = item.Properties?.$values || item.Properties || [];
            propsArray.forEach((prop: any) => { worksite[prop.Name] = prop.Value; });
            return worksite;
          });
          console.log('✅ Worksite lookup found:', worksites.length);
          return { success: true, count: worksites.length, members: worksites, totalCount: data.TotalCount || worksites.length };
        }),
        catchError(error => {
          console.error('❌ Worksite lookup failed:', error);
          return throwError(() => ({ success: false, error: error.message || 'Unknown error', members: [], count: 0 }));
        })
      );
    });
  }

  addSiteVisit(params: AddSiteVisitParams): Observable<SiteVisitResponse> {
    return this.ensureAuthenticated(() => {
      const endpoint = `${this.baseUrl}/api/i4u_UT_SiteVisits`;
      const date = params.date ?? new Date().toLocaleDateString('en-CA') + 'T00:00:00';

      if (!this.loggedInUserId) {
        return throwError(() => new Error('Logged-in user ID not available — could not determine LeadStaff'));
      }

      const body = {
        '$type': 'Asi.Soa.Core.DataContracts.GenericEntityData, Asi.Contracts',
        'EntityTypeName': 'i4u_UT_SiteVisits',
        'PrimaryParentEntityTypeName': 'Party',
        'Properties': {
          '$type': 'Asi.Soa.Core.DataContracts.GenericPropertyDataCollection, Asi.Contracts',
          '$values': [
            { '$type': 'Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts', 'Name': 'ID',        'Value': params.worksiteId },
            { '$type': 'Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts', 'Name': 'VisitType', 'Value': 'SV' },
            { '$type': 'Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts', 'Name': 'Date',      'Value': date },
            { '$type': 'Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts', 'Name': 'LeadStaff', 'Value': this.loggedInUserId }
          ]
        }
      };

      console.log('📍 Adding site visit — date:', date, '| body:', JSON.stringify(body));

      return this.http.post<any>(endpoint, body, { headers: this.getHeaders() }).pipe(
        map(response => {
          console.log('✅ Site visit response:', JSON.stringify(response));
          return { success: true, message: `Site visit recorded for worksite ${params.worksiteId}` };
        }),
        catchError(error => {
          console.error('❌ Failed to add site visit:', error);
          return throwError(() => ({
            success: false,
            message: 'Failed to record site visit',
            error: error.message || 'Unknown error'
          }));
        })
      );
    });
  }

  private buildMemberIqaParams(params: MemberQueryParams): HttpParams {
    let httpParams = new HttpParams().set('QueryName', environment.imis.iqaQueryName);

    const hasAnyFilter =
      params.firstName || params.company || params.lastName || params.gender ||
      params.financialStatus || params.status || params.isMember !== undefined;

    if (hasAnyFilter) {
      httpParams = httpParams.append('parameter', params.firstName ?? '');
      httpParams = httpParams.append('parameter', params.lastName ?? '');
      httpParams = httpParams.append('parameter', params.company ?? '');
      httpParams = httpParams.append('parameter', params.gender ?? '');
      httpParams = httpParams.append('parameter',
        params.financialStatus === 'financial' ? 'financial' :
        params.financialStatus === 'unfinancial' ? 'unfinancial' :
        params.financialStatus === 'non_fee_paying' ? 'non fee paying' : '');
      httpParams = httpParams.append('parameter', params.status ? 'A' : '');
      httpParams = httpParams.append('parameter', params.isMember !== undefined ? (params.isMember ? 'true' : 'false') : '');
    }
    return httpParams;
  }

  private fetchMemberIqaPage(baseParams: HttpParams, offset: number): Observable<QueryResponse> {
    const httpParams = baseParams.set('Limit', '500').set('Offset', String(offset));
    return this.http.get<any>(`${this.baseUrl}/api/iqa`, {
      headers: this.getHeaders(),
      params: httpParams
    }).pipe(
      map(data => {
        const itemsArray: any[] = data.Items?.$values || data.Items || [];
        const members = itemsArray.map((item: any) => {
          const member: any = {};
          const propsArray = item.Properties?.$values || item.Properties || [];
          propsArray.forEach((prop: any) => { member[prop.Name] = prop.Value; });
          return member;
        });
        return {
          success: true,
          count: members.length,
          members,
          totalCount: data.TotalCount || data.Count || 0
        };
      }),
      catchError(error => {
        console.error('❌ IQA page fetch failed (offset ' + offset + '):', error);
          console.error('Status:', error.status);
          console.error('Error details:', error.error);
          return throwError(() => ({
            success: false,
            error: error.message || 'Unknown error',
            members: [],
            count: 0
          }));
        })
      );
  }

  queryMembers(params: MemberQueryParams): Observable<QueryResponse> {
    return this.ensureAuthenticated(() => {
      const baseParams = this.buildMemberIqaParams(params);
      console.log('🔍 Executing IQA query:', environment.imis.iqaQueryName, '| params:', params);

      return this.fetchMemberIqaPage(baseParams, 0).pipe(
        switchMap(firstPage => {
          const total = firstPage.totalCount ?? firstPage.count;
          if (firstPage.count >= total) {
            return of(firstPage);
          }

          const pageSize = 500;
          const offsets: number[] = [];
          for (let offset = pageSize; offset < total; offset += pageSize) {
            offsets.push(offset);
          }

          console.log(`📄 Fetching ${offsets.length} more page(s) for ${total} total records...`);

          return forkJoin(offsets.map(o => this.fetchMemberIqaPage(baseParams, o))).pipe(
            map(pages => {
              const allMembers = [
                ...firstPage.members,
                ...pages.flatMap(p => p.members)
              ];
              console.log('✅ All pages fetched — total records:', allMembers.length);
              return { success: true, count: allMembers.length, members: allMembers, totalCount: total };
            })
          );
        }),
        catchError(error => {
          console.error('❌ queryMembers failed:', error);
          return throwError(() => ({ success: false, error: error.message || 'Unknown error', members: [], count: 0 }));
        })
      );
    });
  }

  /**
   * Execute a saved IQA query
   */
  executeIqaQuery(queryName: string, parameters?: any): Observable<QueryResponse> {
    return this.ensureAuthenticated(() => {
      const endpoint = `${this.baseUrl}/IQA`;
      const params = {
        QueryName: queryName,
        ...parameters
      };
      
      return this.http.get<any>(endpoint, {
        headers: this.getHeaders(),
        params
      }).pipe(
        map(data => ({
          success: true,
          count: data.Items?.length || 0,
          members: data.Items || [],
          totalCount: data.Count || 0
        })),
        catchError(error => {
          console.error('IQA query failed:', error);
          return throwError(() => ({
            success: false,
            error: error.message || 'Unknown error',
            members: [],
            count: 0
          }));
        })
      );
    });
  }

  lookupContact(params: ContactLookupParams): Observable<QueryResponse> {
    return this.ensureAuthenticated(() => {
      let httpParams = new HttpParams()
        .set('QueryName', environment.imis.contactLookupQueryName)
        .append('parameter', params.firstName ?? '')
        .append('parameter', params.lastName ?? '')
        .append('Limit', '50');

      console.log('🔎 Contact lookup:', params);

      return this.http.get<any>(`${this.baseUrl}/api/iqa`, {
        headers: this.getHeaders(),
        params: httpParams
      }).pipe(
        map(data => {
          const itemsArray = data.Items?.$values || data.Items || [];
          const members = itemsArray.map((item: any) => {
            const member: any = {};
            const propsArray = item.Properties?.$values || item.Properties || [];
            propsArray.forEach((prop: any) => { member[prop.Name] = prop.Value; });
            return member;
          });
          console.log('✅ Contact lookup found:', members.length);
          return { success: true, count: members.length, members, totalCount: data.TotalCount || members.length };
        }),
        catchError(error => {
          console.error('❌ Contact lookup failed:', error);
          return throwError(() => ({ success: false, error: error.message || 'Unknown error', members: [], count: 0 }));
        })
      );
    });
  }

  fetchApiKey(): Observable<string> {
    return this.ensureAuthenticated(() => {
      const httpParams = new HttpParams()
        .set('QueryName', '$/_i4u_/Core/StaffAssistantBot/Get API Key')
        .set('Limit', '1');

      return this.http.get<any>(`${this.baseUrl}/api/iqa`, {
        headers: this.getHeaders(),
        params: httpParams
      }).pipe(
        map(data => {
          const itemsArray = data.Items?.$values || data.Items || [];
          if (!itemsArray.length) throw new Error('API key IQA returned no rows');
          const propsArray = itemsArray[0].Properties?.$values || itemsArray[0].Properties || [];
          const codeProp = propsArray.find((p: any) => p.Name === 'Code');
          if (!codeProp?.Value) throw new Error('Code column missing from API key result');
          const key = String(codeProp.Value).trim();
          console.log('🔑 API key fetched from iMIS (length:', key.length, ')');
          return key;
        }),
        catchError(error => {
          console.error('❌ Failed to fetch API key from iMIS:', error);
          return throwError(() => error);
        })
      );
    });
  }

  addInteraction(params: AddInteractionParams): Observable<InteractionResponse> {
    return this.ensureAuthenticated(() => {
      const endpoint = `${this.baseUrl}/api/i4u_UT_Interactions`;
      const date = params.date ?? new Date().toLocaleDateString('en-CA') + 'T00:00:00';

      const prop = (name: string, value: string) => ({
        '$type': 'Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts',
        'Name': name,
        'Value': value
      });

      const values = [
        prop('ID',   params.memberId),
        prop('Date', date),
        prop('Note', params.note),
        prop('Type', params.type ?? 'Note'),
        ...(this.loggedInUserId ? [prop('CreatedBy', this.loggedInUserId)] : [])
      ];

      const body = {
        '$type': 'Asi.Soa.Core.DataContracts.GenericEntityData, Asi.Contracts',
        'EntityTypeName': 'i4u_UT_Interactions',
        'PrimaryParentEntityTypeName': 'Party',
        'Properties': {
          '$type': 'Asi.Soa.Core.DataContracts.GenericPropertyDataCollection, Asi.Contracts',
          '$values': values
        }
      };

      console.log('📝 Adding interaction — date being sent:', date, '| full body:', JSON.stringify(body));

      return this.http.post<any>(endpoint, body, { headers: this.getHeaders() }).pipe(
        map(response => {
          console.log('✅ iMIS interaction response:', JSON.stringify(response));
          return { success: true, message: `Note added successfully for member ${params.memberId}` };
        }),
        catchError(error => {
          console.error('❌ Failed to add interaction:', error);
          return throwError(() => ({
            success: false,
            message: 'Failed to add note',
            error: error.message || 'Unknown error'
          }));
        })
      );
    });
  }
}
